import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, Clock, Send, X } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { formatCurrency } from '../../lib/utils';
import SendStatementModal from '../SendStatementModal';
import type { CashReport, LeadsReport, VelocityReport } from '../../hooks/useControlCentreReports';
import type { ScheduledMonthFigure } from '../../hooks/useMonthSalesReports';
import { useSalesReportDetails, type SalesShare } from '../../hooks/useSalesReportDetails';
import { useLeadsReportDetails, type LeadFeedStatus, type LeadShare, type YtdMonth } from '../../hooks/useLeadsReportDetails';
import { formatDays, formatDuration } from './crmReportPrimitives';
import CrmEmbeddedOpportunityModal from './CrmEmbeddedOpportunityModal';

function percentChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 100);
}

function Trend({ pct }: { pct: number | null }) {
  if (pct === null || pct === 0) return null;
  const up = pct > 0;
  return (
    <span className={`text-[12px] font-medium ${up ? 'text-green-700' : 'text-red-700'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

function Shell({
  open,
  title,
  subtitle,
  labelledBy,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  subtitle: string;
  labelledBy: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[99998] flex items-center justify-center p-3 sm:p-4" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        className="relative flex max-h-[min(92vh,860px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)]"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 pb-3 pt-4">
          <div>
            <h2 id={labelledBy} className="text-base font-semibold text-gray-900">{title}</h2>
            <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-0.5 text-[15px] font-semibold tabular-nums text-gray-900">{value}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function Bar({ value, max, color }: { value: number; max: number; color: string }) {
  const width = max > 0 ? Math.max((value / max) * 100, value > 0 ? 2 : 0) : 0;
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
      <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: color }} />
    </div>
  );
}

export function CashCollectedReportModal({ open, onClose, cash }: { open: boolean; onClose: () => void; cash: CashReport }) {
  const { selectedBusiness } = useBusiness();
  const [statementOpen, setStatementOpen] = useState(false);
  const maxDay = Math.max(...cash.days.map((day) => day.total), 0);
  const channelTotal = cash.channels.reduce((sum, row) => sum + row.amount, 0);

  return (
    <Shell open={open} title="Cash collected" subtitle={cash.monthLabel || 'Month to date'} labelledBy="cash-report-title" onClose={onClose}>
      <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-3">
        {cash.loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : cash.error ? (
          <p className="text-sm text-red-600">{cash.error}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Cash collected this month</p>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <p className="text-2xl font-bold tabular-nums tracking-tight text-gray-900">{formatCurrency(cash.collected)}</p>
                  <Trend pct={percentChange(cash.collected, cash.lastMonth)} />
                </div>
                {cash.credit > 0 ? (
                  <p className="mt-0.5 text-[11px] font-medium tabular-nums text-teal-700">+ {formatCurrency(cash.credit)} stored credit redeemed</p>
                ) : null}
                <p className="mt-0.5 text-[11px] text-gray-500">vs {formatCurrency(cash.lastMonth)} at this point last month</p>
              </div>
              <div className="flex gap-5 sm:gap-7">
                <Stat label="Today" value={formatCurrency(cash.today)} />
                <Stat label="Avg per day" value={formatCurrency(cash.avgPerDay)} />
                <Stat label="Projected month-end" value={formatCurrency(cash.projected)} />
              </div>
            </div>
            <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-gray-200/70 pt-2.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Past due</span>
                {cash.aging.filter((bucket) => bucket.key !== 'current').map((bucket) => (
                  <span key={bucket.key} className="inline-flex items-baseline gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1">
                    <span className="text-[10px] font-medium text-gray-500">{bucket.label}</span>
                    <span className={`text-[12px] font-semibold tabular-nums ${bucket.amount > 0 ? 'text-orange-700' : 'text-gray-400'}`}>{formatCurrency(bucket.amount)}</span>
                    {bucket.count > 0 ? <span className="text-[10px] tabular-nums text-gray-400">{bucket.count}</span> : null}
                  </span>
                ))}
              </div>
              <button type="button" onClick={() => setStatementOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-gray-700 hover:bg-gray-50">
                <Send className="h-3.5 w-3.5" />
                Statement
              </button>
            </div>
          </>
        )}
      </div>
      {!cash.loading && !cash.error ? (
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
          <Section title="Daily collections">
            <div className="flex h-28 items-end gap-1 rounded-xl border border-gray-200 bg-white px-3 py-3">
              {cash.days.map((day) => (
                <div key={day.date} className="flex h-full min-w-0 flex-1 flex-col justify-end" title={`${day.label}: ${formatCurrency(day.total)}`}>
                  <div
                    className={`w-full rounded-sm ${day.isToday ? 'bg-gray-900' : 'bg-blue-500'}`}
                    style={{ height: maxDay > 0 ? `${Math.max((day.total / maxDay) * 100, day.total > 0 ? 4 : 0)}%` : 0 }}
                  />
                </div>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400">Each bar is money received that day, including stored credit. The headline leaves credit out.</p>
          </Section>

          <Section title="Where it came from">
            <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
              {cash.channels.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-gray-500">No payments yet.</p>
              ) : cash.channels.map((row) => (
                <div key={row.key} className="px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className={`text-sm font-medium ${row.credit ? 'text-teal-700' : 'text-gray-800'}`}>
                      {row.label}
                      <span className="ml-1.5 text-[11px] font-normal text-gray-400">{row.count} {row.count === 1 ? 'payment' : 'payments'}</span>
                      {row.credit ? <span className="ml-1.5 text-[10px] font-medium text-teal-700">Not new cash</span> : null}
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-gray-900">
                      {formatCurrency(row.amount)}
                      <span className="ml-1.5 text-[11px] font-normal text-gray-400">{channelTotal > 0 ? Math.round((row.amount / channelTotal) * 100) : 0}%</span>
                    </span>
                  </div>
                  <div className="mt-1.5"><Bar value={row.amount} max={channelTotal} color={row.credit ? '#0f766e' : '#2563eb'} /></div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Inside the number">
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-1 text-sm">
              {([
                ['Cash collected', formatCurrency(cash.collected)],
                ['Stored credit redeemed', formatCurrency(cash.credit)],
                ['Tips', formatCurrency(cash.tips)],
                ['Card surcharges', formatCurrency(cash.surcharges)],
                [`Refunds${cash.refundCount ? ` (${cash.refundCount})` : ''}`, formatCurrency(cash.refunds)],
              ] as const).map(([name, value]) => (
                <div key={name} className="flex items-baseline justify-between gap-3 py-1.5">
                  <span className="text-gray-600">{name}</span>
                  <span className="font-semibold tabular-nums text-gray-900">{value}</span>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-3 border-t border-gray-100 py-1.5">
                <span className="font-medium text-gray-800">Net after refunds</span>
                <span className="font-bold tabular-nums text-gray-900">{formatCurrency(cash.net)}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3 py-1.5">
                <span className="text-gray-600">Invoiced this month</span>
                <span className="font-semibold tabular-nums text-gray-900">{formatCurrency(cash.invoiced)}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3 py-1.5">
                <span className="text-gray-600">Still outstanding · {cash.openInvoices} open</span>
                <span className="font-semibold tabular-nums text-orange-700">{formatCurrency(cash.outstanding)}</span>
              </div>
            </div>
          </Section>

          <Section title="Who paid">
            <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
              {cash.customers.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-gray-500">No payments yet.</p>
              ) : cash.customers.map((row) => (
                <div key={row.name} className="flex items-baseline justify-between gap-3 px-3 py-2.5">
                  <span className="truncate text-sm text-gray-800">
                    {row.name}
                    <span className="ml-1.5 text-[11px] text-gray-400">{row.count}</span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">{formatCurrency(row.amount)}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>
      ) : null}
      {selectedBusiness?.id ? (
        <SendStatementModal
          isOpen={statementOpen}
          onClose={() => setStatementOpen(false)}
          businessId={selectedBusiness.id}
          businessName={selectedBusiness.name ?? ''}
        />
      ) : null}
    </Shell>
  );
}

const LEAD_PILL: Record<LeadFeedStatus, string> = {
  Open: 'bg-blue-50 text-blue-700',
  Won: 'bg-emerald-50 text-emerald-700',
  Lost: 'bg-red-50 text-red-700',
};

const OUTCOME_COLOR: Record<string, string> = {
  Open: '#2563eb',
  Won: '#10b981',
  Lost: '#ef4444',
};

const YTD_METRICS: { key: keyof Pick<YtdMonth, 'newLeads' | 'winRate' | 'conversion' | 'avgDeal' | 'timeToClose' | 'revenue'>; label: string }[] = [
  { key: 'newLeads', label: 'New leads' },
  { key: 'winRate', label: 'Win rate' },
  { key: 'conversion', label: 'Conversion' },
  { key: 'avgDeal', label: 'Avg deal' },
  { key: 'timeToClose', label: 'Time to close' },
  { key: 'revenue', label: 'Revenue won' },
];

function healthPill(winRate: number, hasClosed: boolean): { label: string; className: string } {
  if (!hasClosed) return { label: 'No data yet', className: 'bg-gray-100 text-gray-500' };
  if (winRate >= 60) return { label: 'Healthy', className: 'bg-emerald-50 text-emerald-700' };
  if (winRate >= 40) return { label: 'Steady', className: 'bg-amber-50 text-amber-700' };
  return { label: 'Needs attention', className: 'bg-red-50 text-red-700' };
}

function HealthMove({ current, previous, invert }: { current: number; previous: number; invert?: boolean }) {
  if (previous === 0) {
    if (current <= 0) return null;
    return <span className={`text-[11px] font-medium ${invert ? 'text-red-700' : 'text-green-700'}`}>New</span>;
  }
  const pct = percentChange(current, previous);
  if (pct === null || pct === 0) return null;
  const good = invert ? pct < 0 : pct > 0;
  return (
    <span className={`text-[11px] font-medium ${good ? 'text-green-700' : 'text-red-700'}`}>
      {pct > 0 ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

function LeadShares({ rows, noun, empty, loading, color, money }: { rows: LeadShare[]; noun: string; empty: string; loading?: boolean; color: string | ((row: LeadShare) => string); money?: boolean }) {
  if (loading && rows.length === 0) return <p className="px-3 py-6 text-center text-sm text-gray-400">Loading…</p>;
  if (rows.length === 0) return <p className="px-3 py-6 text-center text-sm text-gray-500">{empty}</p>;
  return (
    <div className="divide-y divide-gray-100">
      {rows.map((row) => {
        const bar = typeof color === 'function' ? color(row) : color;
        return (
          <div key={row.name} className="px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="inline-flex min-w-0 items-baseline gap-1.5 text-sm font-medium text-gray-800">
                <span className="h-2 w-2 shrink-0 self-center rounded-full" style={{ backgroundColor: bar }} />
                <span className="truncate">{row.name}</span>
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                {money ? formatCurrency(row.value) : <>{row.count} {noun}{row.count === 1 ? '' : 's'}</>}
                {money ? <span className="ml-1.5 text-[11px] font-normal text-gray-500">{row.count} won</span> : row.value > 0 ? <span className="ml-1.5 text-[11px] font-normal text-gray-500">{formatCurrency(row.value)}</span> : null}
                <span className="ml-1.5 text-[11px] font-normal text-gray-400">{Math.round(row.share * 100)}%</span>
              </span>
            </div>
            <div className="mt-1.5"><Bar value={row.share} max={1} color={bar} /></div>
          </div>
        );
      })}
    </div>
  );
}

function ytdValue(row: YtdMonth, key: (typeof YTD_METRICS)[number]['key']): string {
  if (key === 'newLeads') return String(row.newLeads);
  if (key === 'winRate' || key === 'conversion') return `${Math.round(row[key])}%`;
  if (key === 'avgDeal' || key === 'revenue') return formatCurrency(row[key]);
  return formatDays(row.timeToClose);
}

export function LeadsReportModal({ open, onClose, leads }: { open: boolean; onClose: () => void; leads: LeadsReport }) {
  const [sourceView, setSourceView] = useState<'new' | 'won'>('new');
  const [feedOpen, setFeedOpen] = useState(false);
  const [ytdOpen, setYtdOpen] = useState(false);
  const [ytdKey, setYtdKey] = useState<(typeof YTD_METRICS)[number]['key']>('newLeads');
  const [selectedLead, setSelectedLead] = useState<number | null>(null);
  const openPipeline = leads.stages.reduce((sum, row) => sum + row.value, 0);
  const details = useLeadsReportDetails(open && !leads.loading && !leads.error, openPipeline);
  const maxDay = Math.max(...leads.days.map((day) => day.total), 0);
  const newSources: LeadShare[] = (() => {
    const total = leads.sources.reduce((sum, row) => sum + row.leads, 0);
    return leads.sources.map((row) => ({
      name: row.name,
      count: row.leads,
      value: row.value,
      share: total > 0 ? row.leads / total : 0,
    }));
  })();
  const sourceRows = sourceView === 'new' ? newSources : details.wonSources;
  const revenueBySource = (() => {
    const rows = [...details.wonSources].sort((a, b) => b.value - a.value || b.count - a.count || a.name.localeCompare(b.name));
    const totalValue = rows.reduce((sum, row) => sum + row.value, 0);
    const totalCount = rows.reduce((sum, row) => sum + row.count, 0);
    return rows.map((row) => ({
      ...row,
      share: totalValue > 0 ? row.value / totalValue : totalCount > 0 ? row.count / totalCount : 0,
    }));
  })();
  const worked = leads.opening + leads.leads;
  const accounted = leads.openNow + leads.won + leads.lost;
  const closedThisMonth = leads.won + leads.lost;
  const pill = healthPill(details.winRate, closedThisMonth > 0);
  const feedDays = details.feed.reduce<Array<{ day: string; label: string; isToday: boolean; rows: typeof details.feed }>>((groups, row) => {
    const last = groups[groups.length - 1];
    if (last && last.day === row.day) last.rows.push(row);
    else groups.push({ day: row.day, label: row.dayLabel, isToday: row.isToday, rows: [row] });
    return groups;
  }, []);
  const ytdMax = Math.max(...details.ytd.map((row) => {
    const value = row[ytdKey];
    return typeof value === 'number' ? value : 0;
  }), 0);

  return (
    <>
    <Shell open={open} title="New leads this month" subtitle={leads.monthLabel || 'Month to date'} labelledBy="leads-report-title" onClose={() => { setSelectedLead(null); onClose(); }}>
      <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-3">
        {leads.loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : leads.error ? (
          <p className="text-sm text-red-600">{leads.error}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">New leads this month</p>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <p className="text-2xl font-bold tabular-nums tracking-tight text-gray-900">{leads.leads}</p>
                  <Trend pct={percentChange(leads.leads, leads.lastMonth)} />
                </div>
                <p className="mt-0.5 text-[11px] text-gray-500">vs {leads.lastMonth} last month</p>
              </div>
              <div className="flex flex-wrap gap-5 sm:gap-7">
                <Stat label="Today" value={String(leads.today)} />
                <Stat label="Avg per day" value={leads.avgPerDay.toFixed(1)} />
                <Stat label="Projected month-end" value={String(Math.round(leads.projected))} />
                <Stat label="Pipeline value" value={formatCurrency(leads.pipelineValue)} />
              </div>
            </div>
            {!details.loading && (details.overdue > 0 || details.stalled > 0) ? (
              <div className="mt-2.5 flex flex-wrap items-center gap-2 border-t border-gray-200/70 pt-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Needs attention</span>
                {details.overdue > 0 ? (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] font-semibold text-red-700">
                    <Clock className="h-3.5 w-3.5" />
                    {details.overdue} overdue follow-up{details.overdue === 1 ? '' : 's'}
                  </span>
                ) : null}
                {details.stalled > 0 ? (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[12px] font-semibold text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {details.stalled} stalled
                  </span>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
      {!leads.loading && !leads.error ? (
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {details.error ? <p className="text-sm text-red-600">{details.error}</p> : null}

          <Section title="Daily new leads">
            <p className="mb-2 text-[11px] text-gray-400">Bars are the day each lead came in, coloured by where it stands now.</p>
            <div className="flex h-28 items-end gap-1 rounded-xl border border-gray-200 bg-white px-3 py-3">
              {leads.days.every((day) => day.total === 0) ? (
                <p className="w-full py-6 text-center text-sm text-gray-500">No leads created this month.</p>
              ) : leads.days.map((day) => (
                <div key={day.date} className="flex h-full min-w-0 flex-1 flex-col justify-end" title={`${day.label}: ${day.total}`}>
                  <div className="flex w-full flex-col justify-end overflow-hidden rounded-sm" style={{ height: maxDay > 0 ? `${Math.max((day.total / maxDay) * 100, day.total > 0 ? 6 : 0)}%` : 0 }}>
                    <div className="bg-red-400" style={{ flex: day.lost }} />
                    <div className="bg-emerald-500" style={{ flex: day.won }} />
                    <div className="bg-blue-500" style={{ flex: day.open }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-1.5 flex gap-3 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" /> Still open</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Won</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-400" /> Lost</span>
            </p>
          </Section>

          <section>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Where they came from</h3>
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                {(['new', 'won'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setSourceView(mode)}
                    className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${sourceView === mode ? 'border border-gray-200 bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {mode === 'new' ? 'New' : 'Won'}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-1 text-[11px] text-gray-400">
              {sourceView === 'new' ? 'Leads created this month, by source.' : 'Deals won this month, by the source that produced them.'}
            </p>
            <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white">
              <LeadShares
                rows={sourceRows}
                noun="lead"
                empty={sourceView === 'new' ? 'No leads yet.' : 'Nothing won this month yet.'}
                loading={sourceView === 'won' && details.loading}
                color="#2563eb"
              />
            </div>
          </section>

          <Section title="Revenue won by source">
            <p className="mb-2 text-[11px] text-gray-400">Value of deals marked won this month, by the source on the lead.</p>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <LeadShares
                rows={revenueBySource}
                noun="deal"
                empty="Nothing won this month."
                loading={details.loading}
                color="#10b981"
                money
              />
            </div>
          </Section>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <section>
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">What happened to them</h3>
              <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white">
                <LeadShares
                  rows={details.outcomes}
                  noun="lead"
                  empty="No leads yet."
                  loading={details.loading}
                  color={(row) => OUTCOME_COLOR[row.name] || '#2563eb'}
                />
                {leads.leads > 0 && !details.loading ? (
                  <div className="flex items-baseline justify-between gap-3 border-t border-gray-100 px-3 py-2 text-sm">
                    <span className="text-gray-600">Conversion rate</span>
                    <span className="font-semibold tabular-nums text-gray-900">{details.conversion.toFixed(0)}%</span>
                  </div>
                ) : null}
                <p className="px-3 pb-2 text-[11px] text-gray-400">Of the leads created this month.</p>
              </div>
            </section>
            <Section title="Open pipeline by stage">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                {leads.stages.length === 0 ? (
                  <p className="px-3 py-6 text-center text-sm text-gray-500">No open leads.</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {leads.stages.map((row) => {
                      const share = leads.openNow > 0 ? row.open / leads.openNow : 0;
                      return (
                        <div key={row.name} className="px-3 py-2.5">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="truncate text-sm font-medium text-gray-800">{row.name}</span>
                            <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                              {row.open} lead{row.open === 1 ? '' : 's'}
                              {row.value > 0 ? <span className="ml-1.5 text-[11px] font-normal text-gray-500">{formatCurrency(row.value)}</span> : null}
                              <span className="ml-1.5 text-[11px] font-normal text-gray-400">{Math.round(share * 100)}%</span>
                            </span>
                          </div>
                          <div className="mt-1.5"><Bar value={row.open} max={leads.openNow} color="#2563eb" /></div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="border-t border-gray-100 px-3 py-2 text-[11px] text-gray-400">All open leads, not just this month's.</p>
              </div>
            </Section>
          </div>

          <Section title="Why we lost">
            <p className="mb-2 text-[11px] text-gray-400">Leads marked lost this month, by reason.</p>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <LeadShares rows={details.lostReasons} noun="lead" empty="Nothing lost this month." loading={details.loading} color="#f87171" />
            </div>
          </Section>

          <section>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Pipeline health</h3>
              {details.loading ? null : (
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${pill.className}`}>{pill.label}</span>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-3">
              {([
                ['Open pipeline', formatCurrency(openPipeline), null, null, false],
                ['Win rate', `${Math.round(details.winRate)}%`, details.winRate, details.winRateLast, false],
                ['Revenue won', formatCurrency(details.revenue), details.revenue, details.revenueLast, false],
                ['Avg deal size', formatCurrency(details.avgDeal), details.avgDeal, details.avgDealLast, false],
                ['Time to close', formatDays(details.timeToClose), details.timeToClose, details.timeToCloseLast, true],
                ['Time to first contact', formatDuration(details.timeToContact), details.timeToContact, details.timeToContactLast, true],
              ] as const).map(([label, value, current, previous, invert]) => (
                <div key={label} className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
                  <div className="mt-0.5 flex items-baseline gap-1.5">
                    <p className="text-[15px] font-semibold tabular-nums text-gray-900">{details.loading && label !== 'Open pipeline' ? '—' : value}</p>
                    {!details.loading && current != null && previous != null ? <HealthMove current={current} previous={previous} invert={invert} /> : null}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <Section title="Inside the number">
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm">
              {([
                ['Opening balance (open on the 1st)', String(leads.opening), 'text-blue-700', false],
                ['New leads this month', `+ ${leads.leads}`, 'text-gray-900', false],
                ['Total worked', String(worked), 'text-gray-900', true],
                ['Still open', String(leads.openNow), 'text-blue-700', true],
                ['Won this month', String(leads.won), 'text-emerald-700', false],
                ['Lost this month', String(leads.lost), 'text-red-700', false],
                ['Accounted for', String(accounted), accounted === worked ? 'text-gray-900' : 'text-amber-700', true],
              ] as const).map(([label, value, color, divider]) => (
                <div key={label} className={`flex items-baseline justify-between gap-3 py-1.5 ${divider ? 'border-t border-gray-100' : ''}`}>
                  <span className={divider && label !== 'Still open' ? 'font-medium text-gray-800' : 'text-gray-600'}>{label}</span>
                  <span className={`font-semibold tabular-nums ${color}`}>{value}</span>
                </div>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400">Opening plus new equals open now plus won plus lost. Won and lost are leads marked that way this month, including ones that came in earlier.</p>
          </Section>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <button type="button" onClick={() => setFeedOpen((value) => !value)} className="flex w-full items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50" aria-expanded={feedOpen}>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Leads this month{leads.leads > 0 ? ` · ${leads.leads}` : ''}
              </span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${feedOpen ? 'rotate-180' : ''}`} />
            </button>
            {feedOpen ? (
              details.loading ? (
                <p className="border-t border-gray-100 px-3 py-6 text-center text-sm text-gray-400">Loading…</p>
              ) : feedDays.length === 0 ? (
                <p className="border-t border-gray-100 px-3 py-6 text-center text-sm text-gray-500">No leads created this month.</p>
              ) : (
                <div className="border-t border-gray-100">
                  {feedDays.map((day) => (
                    <div key={day.day || 'undated'}>
                      <div className={`border-b border-gray-100 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide ${day.isToday ? 'bg-blue-50/80 text-blue-700' : 'bg-gray-50/80 text-gray-500'}`}>
                        {day.label}{day.isToday ? ' · Today' : ''}
                      </div>
                      <ul className="divide-y divide-gray-50">
                        {day.rows.map((row) => (
                          <li key={row.id}>
                            <button type="button" onClick={() => row.id > 0 && setSelectedLead(row.id)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-gray-800">{row.title}</p>
                                <p className="truncate text-[11px] text-gray-500">{row.subtitle}</p>
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                {row.value > 0 ? <span className="text-sm font-semibold tabular-nums text-gray-900">{formatCurrency(row.value)}</span> : null}
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${LEAD_PILL[row.status]}`}>{row.status}</span>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )
            ) : null}
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <button type="button" onClick={() => setYtdOpen((value) => !value)} className="flex w-full items-center justify-between gap-3 px-3 py-2.5 hover:bg-gray-50" aria-expanded={ytdOpen}>
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Year to date trends</span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${ytdOpen ? 'rotate-180' : ''}`} />
            </button>
            {ytdOpen ? (
              <div className="border-t border-gray-100 px-3 py-3">
                <div className="flex flex-wrap gap-1">
                  {YTD_METRICS.map((metric) => (
                    <button
                      key={metric.key}
                      type="button"
                      onClick={() => setYtdKey(metric.key)}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${ytdKey === metric.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                      {metric.label}
                    </button>
                  ))}
                </div>
                {details.loading ? (
                  <p className="py-6 text-center text-sm text-gray-400">Loading…</p>
                ) : (
                  <div className="mt-3 space-y-2">
                    {details.ytd.map((row) => {
                      const amount = row[ytdKey];
                      const value = typeof amount === 'number' ? amount : 0;
                      return (
                        <div key={row.month}>
                          <div className="flex items-baseline justify-between gap-3 text-sm">
                            <span className={row.isCurrent ? 'font-semibold text-blue-700' : 'text-gray-700'}>{row.label}{row.isCurrent ? ' · This month' : ''}</span>
                            <span className="font-semibold tabular-nums text-gray-900">{ytdValue(row, ytdKey)}</span>
                          </div>
                          <div className="mt-1"><Bar value={value} max={ytdMax} color={row.isCurrent ? '#2563eb' : '#94a3b8'} /></div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </Shell>
    {selectedLead != null ? (
      <CrmEmbeddedOpportunityModal opportunityId={selectedLead} onClose={() => setSelectedLead(null)} />
    ) : null}
    </>
  );
}

function ShareList({ rows, color, noun, empty }: { rows: SalesShare[]; color: string | ((row: SalesShare) => string); noun: string; empty: string }) {
  if (rows.length === 0) return <p className="px-3 py-6 text-center text-sm text-gray-500">{empty}</p>;
  return (
    <div className="divide-y divide-gray-100">
      {rows.map((row) => {
        const barColor = typeof color === 'function' ? color(row) : color;
        return (
          <div key={row.key} className="px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="inline-flex min-w-0 items-baseline gap-1.5 text-sm font-medium text-gray-800">
                <span className="h-2 w-2 shrink-0 self-center rounded-full" style={{ backgroundColor: barColor }} />
                <span className="truncate">{row.label}</span>
                <span className="shrink-0 text-[11px] font-normal text-gray-400">{row.count} {noun}{row.count === 1 ? '' : 's'}</span>
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                {formatCurrency(row.amount)}
                <span className="ml-1.5 text-[11px] font-normal text-gray-400">{Math.round(row.share * 100)}%</span>
              </span>
            </div>
            <div className="mt-1.5"><Bar value={row.share} max={1} color={barColor} /></div>
          </div>
        );
      })}
    </div>
  );
}

function MoneyLine({ name, value, tone = 'default', strong = false, divider = false }: { name: string; value: string; tone?: 'default' | 'warn' | 'minus'; strong?: boolean; divider?: boolean }) {
  const colour = tone === 'warn' ? 'text-orange-700' : tone === 'minus' ? 'text-red-700' : 'text-gray-900';
  return (
    <div className={`flex items-baseline justify-between gap-3 py-1.5 ${divider ? 'border-t border-gray-100' : ''}`}>
      <span className={strong ? 'font-medium text-gray-800' : 'text-gray-600'}>{name}</span>
      <span className={`tabular-nums ${strong ? 'font-bold' : 'font-semibold'} ${colour}`}>{value}</span>
    </div>
  );
}

/** Expanded view behind the Sales card: issued sales this month plus what is still scheduled. */
export function SalesReportModal({ open, onClose, scheduled }: { open: boolean; onClose: () => void; scheduled: ScheduledMonthFigure }) {
  const sales = useSalesReportDetails(open);
  const maxDay = Math.max(...sales.days.map((day) => day.total), 0);
  const combined = sales.totalSales + scheduled.total;

  return (
    <Shell open={open} title="Sales this month" subtitle={sales.monthLabel || 'Month to date'} labelledBy="sales-report-title" onClose={onClose}>
      <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-3">
        {sales.loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : sales.error ? (
          <p className="text-sm text-red-600">{sales.error}</p>
        ) : (
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Sales this month</p>
              <div className="mt-0.5 flex items-baseline gap-2">
                <p className="text-2xl font-bold tabular-nums tracking-tight text-gray-900">{formatCurrency(sales.totalSales)}</p>
                <Trend pct={percentChange(sales.totalSales, sales.lastMonth)} />
              </div>
              <p className="mt-0.5 text-[11px] text-gray-500">vs {formatCurrency(sales.lastMonth)} at this point last month</p>
            </div>
            <div className="flex flex-wrap gap-5 sm:gap-7">
              <Stat label="Today" value={formatCurrency(sales.today)} />
              <Stat label="Avg per day" value={formatCurrency(sales.avgPerDay)} />
              <Stat label="Projected month-end" value={formatCurrency(sales.projected)} />
              <Stat label="Sales" value={String(sales.orders)} />
              <Stat label="Avg sale" value={formatCurrency(sales.averageSale)} />
            </div>
          </div>
        )}
      </div>
      {!sales.loading && !sales.error ? (
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
          <Section title="Daily sales">
            <div className="flex h-28 items-end gap-1 rounded-xl border border-gray-200 bg-white px-3 py-3">
              {sales.days.map((day) => (
                <div key={day.date} className="flex h-full min-w-0 flex-1 flex-col justify-end" title={`${day.label}: ${formatCurrency(day.total)} · ${day.orders} ${day.orders === 1 ? 'sale' : 'sales'}`}>
                  <div
                    className={`w-full rounded-sm ${day.isToday ? 'bg-gray-900' : 'bg-blue-500'}`}
                    style={{ height: maxDay > 0 ? `${Math.max((day.total / maxDay) * 100, day.total > 0 ? 4 : 0)}%` : 0 }}
                  />
                </div>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400">By the day the invoice was issued. Pre-tax, after discounts.</p>
          </Section>

          <Section title="Still scheduled this month">
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-1 text-sm">
              <MoneyLine name={`Upcoming jobs · ${scheduled.upcoming.count}`} value={formatCurrency(scheduled.upcoming.total)} />
              {scheduled.pastDue.count > 0 ? (
                <MoneyLine name={`Past date, not invoiced · ${scheduled.pastDue.count}`} value={formatCurrency(scheduled.pastDue.total)} tone="warn" />
              ) : null}
              <MoneyLine name="Sales plus scheduled" value={formatCurrency(combined)} strong divider />
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400">Jobs dated this month that are not yet completed or invoiced, at their booked amount. Past-date jobs are worth chasing.</p>
          </Section>

          <Section title="Where it came from">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <ShareList rows={sales.sources} color="#2563eb" noun="sale" empty="No sales yet." />
            </div>
          </Section>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Section title="Who's buying">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <ShareList rows={sales.customerTypes} color={(row) => (row.key === 'new' ? '#10b981' : row.key === 'returning' ? '#2563eb' : '#9ca3af')} noun="sale" empty="No sales yet." />
              </div>
              <p className="mt-1.5 text-[11px] text-gray-400">{sales.customers} customers · {sales.newCustomers} new · {sales.returningCustomers} repeat</p>
            </Section>
            <Section title="Top services">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <ShareList rows={sales.services} color="#f59e0b" noun="sale" empty="No services sold yet." />
              </div>
            </Section>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Section title="By team member">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <ShareList rows={sales.teamMembers} color="#8b5cf6" noun="sale" empty="No jobs assigned yet." />
              </div>
              <p className="mt-1.5 text-[11px] text-gray-400">Shared jobs split the money evenly.</p>
            </Section>
            <Section title="Type of sale">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <ShareList rows={sales.saleTypes} color="#0f766e" noun="sale" empty="No sales yet." />
              </div>
            </Section>
          </div>

          <Section title="Inside the number">
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-1 text-sm">
              <MoneyLine name="Gross sales" value={formatCurrency(sales.grossSales)} />
              <MoneyLine name="Discounts" value={sales.discounts > 0 ? `−${formatCurrency(sales.discounts)}` : formatCurrency(0)} tone={sales.discounts > 0 ? 'minus' : 'default'} />
              <MoneyLine name="Sales this month" value={formatCurrency(sales.totalSales)} strong divider />
              <MoneyLine name="Tax" value={`+ ${formatCurrency(sales.tax)}`} />
              <MoneyLine name="Invoiced" value={formatCurrency(sales.totalInvoiced)} strong divider />
              <MoneyLine name="Paid so far" value={formatCurrency(sales.paid)} />
              <MoneyLine name="Still owing" value={formatCurrency(sales.owing)} tone={sales.owing > 0 ? 'warn' : 'default'} />
              <MoneyLine name="Tips" value={formatCurrency(sales.tips)} divider />
              <MoneyLine name="Card surcharges" value={formatCurrency(sales.surcharges)} />
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400">Sales are pre-tax and count toward the month the invoice was issued. Tips and surcharges are collected with sales but are not sales.</p>
          </Section>
        </div>
      ) : null}
    </Shell>
  );
}

export function SalesVelocityModal({ open, onClose, velocity, monthLabel }: { open: boolean; onClose: () => void; velocity: VelocityReport; monthLabel: string }) {
  return (
    <Shell open={open} title="Sales velocity" subtitle={monthLabel || 'Month to date'} labelledBy="velocity-report-title" onClose={onClose}>
      <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-3">
        {velocity.loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Revenue per day</p>
            <div className="mt-0.5 flex items-baseline gap-2">
              <p className="text-2xl font-bold tabular-nums tracking-tight text-gray-900">{formatCurrency(velocity.perDay)}<span className="ml-1 text-[14px] font-medium text-gray-500">/day</span></p>
              <Trend pct={percentChange(velocity.perDay, velocity.previousPerDay)} />
            </div>
            <p className="mt-0.5 text-[11px] text-gray-500">vs {formatCurrency(velocity.previousPerDay)}/day last month</p>
          </div>
        )}
      </div>
      {!velocity.loading ? (
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
          <p className="text-sm leading-relaxed text-gray-600">
            New leads per day, times the average value of deals won over the last six months, times how often leads created one to three months ago have been won.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Leads per day</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{velocity.leadsPerDay.toFixed(1)}</p>
              <p className="mt-0.5 text-[11px] text-gray-400">{velocity.leadsPerDayLastMonth.toFixed(1)} per day last month</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Average won deal</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{formatCurrency(velocity.avgDeal)}</p>
              <p className="mt-0.5 text-[11px] text-gray-400">{velocity.trailingDeals} won in the last 6 months</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Conversion</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">{Math.round(velocity.conversion)}%</p>
              <p className="mt-0.5 text-[11px] text-gray-400">{velocity.cohortWon} of {velocity.cohortLeads} leads from 30–90 days ago</p>
            </div>
          </div>
        </div>
      ) : null}
    </Shell>
  );
}
